/**
 * Lessons Learned Slide
 * Strategic insights: MCP, legacy B2C swimlanes, platform opportunity
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { media } from '@config/breakpoints';
import {
  SlideBase,
  SlideContent,
  SlideHeader,
  SlideAccentGlow,
  SlideAccentGlowBottom,
  SegmentCard,
  DarkAccentBox,
  DarkCardBody,
  CardRow,
  SlideLabel,
} from '@components/press/pitch-deck/slide-primitives';

/* -------------------------------------------------- */
/* Local styled components                            */
/* -------------------------------------------------- */

const LessonCard = styled(SegmentCard)`
  min-width: 240px;

  ${media.md} {
    padding: ${spacing.xl} ${spacing['2xl']};
  }
`;

const LessonHeading = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.base}, 1.6vw, ${typography.fontSize.lg});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
  margin: 0;
  line-height: ${typography.lineHeight.tight};
`;

const LessonBody = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.1vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  margin: 0;
  line-height: ${typography.lineHeight.relaxed};
`;

const LessonEmphasis = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
`;

const TakeawayBox = styled(DarkAccentBox)`
  max-width: 800px;
  text-align: center;
  padding: ${spacing.lg} ${spacing.xl};
  border-radius: ${borderRadius.xl};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

  &::after {
    inset: 5px;
    border-radius: calc(${borderRadius.xl} - 4px);
  }

  ${media.md} {
    padding: ${spacing.lg} ${spacing.xl};

    &::after {
      inset: 5px;
      border-radius: calc(${borderRadius.xl} - 4px);
    }
  }
`;

const TakeawayText = styled(DarkCardBody)`
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});
  color: rgba(255, 255, 255, 0.9);
`;

const TakeawayHighlight = styled.span`
  color: ${colors.brand.primary};
  font-weight: ${typography.fontWeights.medium};
`;

/* -------------------------------------------------- */
/* Component                                          */
/* -------------------------------------------------- */

const ACCENT = {
  mcp: colors.accent.coral.exxtraDark,
  swimlane: colors.accent.blue.exxtraDark,
  platform: colors.accent.teal.exxtraDark,
};

export const LessonsLearnedSlide: React.FC = () => (
  <SlideBase>
    <SlideAccentGlow />
    <SlideAccentGlowBottom />
    <SlideContent>
      <SlideHeader
        title="Lessons Learned"
        subtitle="What building in AI-first household management has taught us"
      />

      <CardRow>
        {/* ---- Lesson 1: MCP ---- */}
        <LessonCard $accent={ACCENT.mcp}>
          <SlideLabel $color={ACCENT.mcp}>Lesson 1</SlideLabel>
          <LessonHeading>MCP Is a Bad Business Model</LessonHeading>
          <LessonBody>
            The Model Context Protocol promises universal tool interop, but it
            commoditizes the integration layer&mdash;the very place where
            product value lives.{' '}
            <LessonEmphasis>
              If anyone can plug in, no one owns the experience.
            </LessonEmphasis>{' '}
            Families need curated, trustworthy outcomes, not an open bazaar of
            unvetted capabilities.
          </LessonBody>
        </LessonCard>

        {/* ---- Lesson 2: B2C Swimlanes ---- */}
        <LessonCard $accent={ACCENT.swimlane}>
          <SlideLabel $color={ACCENT.swimlane}>Lesson 2</SlideLabel>
          <LessonHeading>Legacy B2C Swimlanes Are an Albatross</LessonHeading>
          <LessonBody>
            Calendars, meal planners, budgeting apps&mdash;each locked in its
            own vertical.{' '}
            <LessonEmphasis>
              Families don&apos;t live in swimlanes.
            </LessonEmphasis>{' '}
            Cross-cutting tasks (birthday party = budget + calendar + shopping +
            invites) expose the seams. Incumbents can&apos;t unify because their
            moats are the walls.
          </LessonBody>
        </LessonCard>

        {/* ---- Lesson 3: Platform Opportunity ---- */}
        <LessonCard $accent={ACCENT.platform}>
          <SlideLabel $color={ACCENT.platform}>Lesson 3</SlideLabel>
          <LessonHeading>
            Platforms That Tie Together Short Software Win
          </LessonHeading>
          <LessonBody>
            Individual AI features have a short half-life&mdash;anyone can ship
            a meal-plan generator.{' '}
            <LessonEmphasis>
              The durable advantage is the connective tissue:
            </LessonEmphasis>{' '}
            context that flows between tasks, personas, and time horizons.
            The platform that stitches short-lived tools into a coherent
            household OS captures the value.
          </LessonBody>
        </LessonCard>
      </CardRow>

      <TakeawayBox>
        <TakeawayText>
          The opportunity is not another point solution.
          It is the <TakeawayHighlight>integration platform</TakeawayHighlight>{' '}
          that earns trust, spans every household persona, and turns
          fragmented AI features into a <TakeawayHighlight>unified family operating system</TakeawayHighlight>.
        </TakeawayText>
      </TakeawayBox>
    </SlideContent>
  </SlideBase>
);
